import { Routes, Route, Navigate } from 'react-router-dom'
import { lazy, Suspense, useEffect } from 'react'
import { authService, useAuth } from '@bri/shared-components'

// Import pages
const DashboardPage = lazy(() => import('./pages/DashboardPage'))
const UsersPage = lazy(() => import('./pages/UsersPage'))
const UserDetailPage = lazy(() => import('./pages/UserDetailPage'))
const WorkspacesPage = lazy(() => import('./pages/WorkspacesPage'))
const WorkspaceDetailPage = lazy(() => import('./pages/WorkspaceDetailPage'))
const ProjectsPage = lazy(() => import('./pages/ProjectsPage'))
const QualUsagePage = lazy(() => import('./pages/QualUsagePage'))
const LogsPage = lazy(() => import('./pages/LogsPage'))
const LoginPage = lazy(() => import('./pages/LoginPage'))
const MaintenancePage = lazy(() => import('./pages/MaintenancePage'))

// Import layout
import AdminLayout from './components/layout/AdminLayout'

function RouteFallback() {
  return (
    <div className="min-h-[320px] flex items-center justify-center bg-admin-bg">
      <div className="loading-spinner w-8 h-8"></div>
    </div>
  )
}

function App() {
  const { user, isLoading } = useAuth()

  useEffect(() => {
    const onError = (event: ErrorEvent) => {
      void authService.sendFrontendTelemetry('frontend.error', 'admin-panel', {
        message: event.message,
        source: event.filename,
        line: event.lineno,
        column: event.colno,
      })
    }

    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      void authService.sendFrontendTelemetry('frontend.unhandled_rejection', 'admin-panel', {
        reason: typeof event.reason === 'string' ? event.reason : String(event.reason),
      })
    }

    window.addEventListener('error', onError)
    window.addEventListener('unhandledrejection', onUnhandledRejection)

    return () => {
      window.removeEventListener('error', onError)
      window.removeEventListener('unhandledrejection', onUnhandledRejection)
    }
  }, [])

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-admin-bg">
        <div className="text-center">
          <div className="loading-spinner w-8 h-8 mx-auto mb-4"></div>
          <p className="text-admin-muted">Loading Admin Panel...</p>
        </div>
      </div>
    )
  }

  // If user is not authenticated, show login
  if (!user) {
    return (
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Suspense>
    )
  }

  // User is authenticated - show admin panel
  return (
    <AdminLayout>
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* Dashboard */}
          <Route path="/" element={<DashboardPage />} />
          
          {/* Users */}
          <Route path="/users" element={<UsersPage />} />
          <Route path="/users/:id" element={<UserDetailPage />} />
          
          {/* Workspaces */}
          <Route path="/workspaces" element={<WorkspacesPage />} />
          <Route path="/workspaces/:id" element={<WorkspaceDetailPage />} />
          
          {/* Projects */}
          <Route path="/projects" element={<ProjectsPage />} />
          <Route path="/qual-usage" element={<QualUsagePage />} />
          
          {/* Logs */}
          <Route path="/logs" element={<LogsPage />} />
          <Route path="/logs/:type" element={<LogsPage />} />

          {/* Maintenance */}
          <Route path="/maintenance" element={<MaintenancePage />} />
          
          {/* Redirect login to dashboard if already authenticated */}
          <Route path="/login" element={<Navigate to="/" replace />} />
          
          {/* Catch all - redirect to dashboard */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </AdminLayout>
  )
}

export default App




